import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, ShieldCheck, BadgeCheck, Clock, ArrowRight, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { LaunchCountdown } from "./LaunchCountdown";

/**
 * PULA LEARN — landing page (PRE-LAUNCH mode).
 * PORTABLE: copy this file + LearnBundlesPage + LearnVerifyPage + AdminLearnWaitlistPage
 * to the pulalove.com frontend unchanged (same React/Tailwind/api-lib stack).
 * Compliance: no accreditation claims while CPD_ACCREDITED=false (server-driven).
 */

export default function LearnLandingPage() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/learn/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const accredited = cfg?.cpd_accredited === true;

  return (
    <main className="bg-white" data-testid="learn-landing-page">
      {/* Hero */}
      <section className="mv-green-bg text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }} />
        <div className="max-w-4xl mx-auto px-4 md:px-8 relative">
          <div className="flex items-center gap-2 mb-6" data-testid="learn-status-badge">
            {accredited ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full mv-gold-bg text-gray-900">
                <BadgeCheck size={14} /> CPD Accredited — Provider No. {cfg?.cpd_provider_number}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white/15 text-white border border-white/25">
                <Clock size={14} /> PRE-LAUNCH — Awaiting CPD Accreditation
              </span>
            )}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            Pula Learn
          </h1>
          <p className="text-base md:text-lg text-white/85 max-w-2xl mb-4">
            Practical, affordable training for UK care workers — built by people who
            understand care. Starting with Learning Disability &amp; Autism training
            aligned to the Health and Care Act 2022 requirement.
          </p>
          <p className="text-sm text-white/70 max-w-2xl mb-10">
            From the Pula Community — the same family of services supporting Southern
            African people at home and in the diaspora.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/learn/bundles" data-testid="learn-view-bundles-btn"
              className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors">
              View Training Bundles <ArrowRight size={18} />
            </Link>
            <Link to="/verify" data-testid="learn-verify-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold border border-white/40 text-white hover:bg-white/10 transition-colors">
              Verify a Certificate
            </Link>
          </div>
        </div>
      </section>

      {/* Launch countdown (only when CPD gives a date) */}
      <LaunchCountdown launchDate={cfg?.launch_date} accredited={accredited} />

      {/* Pre-launch notice */}
      {!accredited && (
        <section className="border-b mv-hairline bg-amber-50" data-testid="learn-prelaunch-banner">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-5 flex gap-3">
            <AlertTriangle size={20} className="shrink-0 mt-0.5 text-amber-600" />
            <p className="text-sm text-amber-900">
              <strong>Pre-launch notice:</strong> Pula Learn has applied for accreditation with the
              CPD Standards Office (UK) and is currently under assessment. Courses are available
              for preview, but accredited certificates cannot be issued until accreditation is
              confirmed. Joining today adds you to the launch waiting list at no charge.
            </p>
          </div>
        </section>
      )}

      {/* Value props */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 md:px-8 grid md:grid-cols-3 gap-8">
          {[
            { icon: GraduationCap, t: "Built for real care work",
              d: "Plain-language modules with scenarios from domiciliary and residential care — not recycled corporate slides." },
            { icon: ShieldCheck, t: "Compliance-first",
              d: "Aligned to the Health and Care Act 2022 (s.181) and the Core Capabilities Frameworks. Accreditation applied for with the CPD Standards Office." },
            { icon: BadgeCheck, t: "Verifiable certificates",
              d: "Every certificate will carry a unique ID that employers can check instantly on our public verification page." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="p-6 rounded-xl border mv-hairline bg-white" data-testid={`learn-value-${t.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}>
              <Icon size={28} className="mv-green-text mb-4" />
              <h2 className="text-base md:text-lg font-semibold mb-2">{t}</h2>
              <p className="text-sm text-gray-600">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Flagship course teaser */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="rounded-2xl p-8 md:p-10 mv-green-bg text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ background: "radial-gradient(500px 250px at 90% 100%, var(--mv-gold), transparent 60%)" }} />
            <div className="relative">
              <p className="text-xs uppercase tracking-widest mv-gold-text font-semibold mb-3">Flagship course</p>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Understanding Learning Disability and Autism
              </h2>
              <p className="text-white/85 text-sm md:text-base max-w-2xl mb-6">
                Four modules covering why the law changed, what learning disability and autism
                actually are, communication that works, and rights-based safe care. Designed to
                align with the statutory training requirement introduced by the Health and Care
                Act 2022 (s.181).
              </p>
              <Link to="/learn/bundles" data-testid="learn-flagship-cta"
                className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors">
                See bundles from £15 <ArrowRight size={16} />
              </Link>
              <Link to="/learn/course/learning-disability-autism-tier1" data-testid="learn-preview-course-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm border border-white/40 text-white hover:bg-white/10 transition-colors ml-3">
                Preview the course free
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
